import { ChangeDetectionStrategy, Component, booleanAttribute, computed, effect, input, output, signal } from '@angular/core';
import { Bucket } from '../../types';
import { UiComboboxOption } from '../../ui/combobox/combobox-option';
import { UiFieldComponent } from '../../ui/field/field.component';
import { UiComboboxComponent } from '../../ui/combobox/combobox.component';

@Component({
    selector: 'app-select-bucket-typeahead',
    templateUrl: './select-bucket-typeahead.component.html',
    styleUrl: './select-bucket-typeahead.component.scss',
    imports: [UiFieldComponent, UiComboboxComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectBucketTypeaheadComponent {
  private static nextId = 0;
  private readonly fundBalanceFormatter = new Intl.NumberFormat('en-US', {
    currency: 'USD',
    maximumFractionDigits: 0,
    style: 'currency',
  });

  readonly label = input('Select Budget');
  readonly hideLabel = input(false, { transform: booleanAttribute });
  readonly placeholder = input('');
  /** Marks the selected budget as a suggestion: sparkles icon and the dashed accent trigger. */
  readonly suggested = input(false, { transform: booleanAttribute });
  readonly required = input(false, { transform: booleanAttribute });
  readonly buckets = input<Bucket[] | null | undefined>([]);
  readonly selectedBucket = input<Bucket | undefined>();
  readonly options = input<{ showFundBalance?: boolean } | null | undefined>({});
  readonly selectedBucketChange = output<Bucket | undefined>();

  readonly inputId = `select-bucket-typeahead-input-${SelectBucketTypeaheadComponent.nextId++}`;

  readonly bucketOptions = computed<UiComboboxOption<Bucket>[]>(() =>
    (this.buckets() ?? []).map(bucket => this.toBucketOption(bucket)),
  );
  readonly panelOpen = signal(false);
  readonly selectedBucketOption = signal<UiComboboxOption<Bucket> | null>(null);

  constructor() {
    effect(() => this.syncSelectedOption(this.selectedBucket()));
  }

  onBucketOptionChange(option: UiComboboxOption<Bucket> | null): void {
    this.selectedBucketOption.set(option);
    this.selectedBucketChange.emit(option?.value);
    this.panelOpen.set(false);
  }

  private toBucketOption(bucket: Bucket): UiComboboxOption<Bucket> {
    return {
      key: bucket.id,
      label: bucket.name,
      value: bucket,
      description: this.options()?.showFundBalance ? this.formatFundBalance(bucket) : undefined,
    };
  }

  private formatFundBalance(bucket: Bucket): string {
    return this.fundBalanceFormatter.format(bucket.fund?.balance ?? 0);
  }

  private syncSelectedOption(selectedBucket: Bucket | undefined): void {
    this.selectedBucketOption.set(
      selectedBucket
        ? this.bucketOptions().find(option => option.key === selectedBucket.id) ?? this.toBucketOption(selectedBucket)
        : null,
    );
  }
}
